import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, CircularProgress, Container, Link as MuiLink, IconButton } from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { ThumbUp, ThumbDown, Send, MoreHoriz } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useLocation, Link } from 'react-router-dom';
import config from '../config';
import AppAppBar from '../components/AppAppBar';
import { checkAuthStatus, getAuthToken, authenticatedRequest } from '../utils/api';

const server = config.server;

const TitleTypography = styled(Typography)({
  position: 'relative',
  textDecoration: 'none',
  whiteSpace: 'pre-line',
  wordBreak: 'break-word',
  fontFamily: 'Noto Sans CJK, Microsoft JhengHei, Arial, sans-serif',
});

function utc2local(utc_str) {
  if (!utc_str) return '';
  const utc = new Date(utc_str);
  const offset = utc.getTimezoneOffset();
  const local = new Date(utc.getTime() - offset * 60000);
  return `${local.getFullYear()}-${String(local.getMonth()+1).padStart(2,'0')}-${String(local.getDate()).padStart(2,'0')} ${String(local.getHours()).padStart(2,'0')}:${String(local.getMinutes()).padStart(2,'0')}:${String(local.getSeconds()).padStart(2,'0')}`;
}

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <MuiLink key={i} href={part} target="_blank" rel="noopener noreferrer"
        sx={{ wordBreak: 'break-all', color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
        {part}
      </MuiLink>
    ) : (
      <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>
    )
  );
};

// ── 內嵌評論區 ────────────────────────────────────────────────────────────────
function InlineComments({ tweet, isAuthenticated }) {
  const location = useLocation();
  const [comments, setComments] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [newCommentText, setNewCommentText] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const fetchComments = async (pageToFetch = 0) => {
    if (!tweet) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${server}/api/tweets/${tweet.tweet_id}/comments?page=${pageToFetch}&limit=10`);
      const data = await res.json();
      const sorted = Array.isArray(data) ? [...data].sort((a, b) => (b.likes||0) - (a.likes||0)) : [];
      if (pageToFetch === 0) setComments(sorted);
      else setComments(prev => [...prev, ...sorted]);
      if (!Array.isArray(data) || data.length < 10) setHasMore(false);
      setPage(pageToFetch);
    } catch (err) {
      console.error('無法獲取評論：', err);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchComments(0);
  }, [tweet?.tweet_id]);

  const handlePostComment = async () => {
    if (!newCommentText.trim()) { alert('請輸入評論內容！'); return; }
    if (newCommentText.length > 1000) { alert('評論不得超過 1000 字！'); return; }
    const token = getAuthToken();
    if (!token) { alert('請登入後再發布評論！'); return; }
    try {
      const res = await authenticatedRequest(`/tweets/${tweet.tweet_id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ comment_text: newCommentText })
      });
      if (!res) return;
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [newComment, ...prev].sort((a, b) => (b.likes||0) - (a.likes||0)));
        setNewCommentText('');
      } else {
        alert(`發布評論失敗：${res.status}`);
      }
    } catch (err) {
      alert('網路錯誤，請稍後再試。');
    }
  };

  const handleVote = async (commentId, is_like_action) => {
    const token = getAuthToken();
    if (!token) { alert('請登入後再點讚/點踩！'); return; }
    try {
      const res = await authenticatedRequest(`/comments/${commentId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ is_like: is_like_action })
      });
      if (!res) return;
      if (res.ok) {
        let resultJson = null;
        try { resultJson = await res.json(); } catch { resultJson = null; }
        if (resultJson && (typeof resultJson.likes === 'number' || typeof resultJson.dislikes === 'number')) {
          setComments(prev =>
            prev.map(c => c.id === commentId ? { ...c, likes: resultJson.likes, dislikes: resultJson.dislikes } : c)
                .sort((a, b) => (b.likes||0) - (a.likes||0))
          );
        } else {
          const msg = resultJson?.message || '';
          setComments(prev =>
            prev.map(c => {
              if (c.id !== commentId) return c;
              let likes = c.likes || 0, dislikes = c.dislikes || 0;
              if (msg.includes('removed')) { if (is_like_action) likes = Math.max(0, likes-1); else dislikes = Math.max(0, dislikes-1); }
              else if (msg.includes('changed')) { if (is_like_action) { likes += 1; dislikes = Math.max(0, dislikes-1); } else { dislikes += 1; likes = Math.max(0, likes-1); } }
              else { if (is_like_action) likes += 1; else dislikes += 1; }
              return { ...c, likes, dislikes };
            }).sort((a, b) => (b.likes||0) - (a.likes||0))
          );
        }
      } else {
        alert('投票失敗，請稍後再試。');
      }
    } catch (err) {
      alert('投票失敗，請稍後再試。');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* 評論標題 */}
      <Typography variant="h6" sx={{ fontFamily: 'Microsoft JhengHei', mb: 2, pb: 1, borderBottom: '2px solid #eee' }}>
        評論（{tweet?.comment_count ?? comments.length}）
      </Typography>

      {/* 評論輸入框 */}
      {isAuthenticated ? (
        <Box sx={{ mb: 3 }}>
          <TextareaAutosize
            minRows={3}
            maxRows={8}
            placeholder="留下你的評論（最多 1000 字）..."
            value={newCommentText}
            onChange={(e) => { if (e.target.value.length <= 1000) setNewCommentText(e.target.value); }}
            style={{
              width: '100%',
              fontFamily: 'Microsoft JhengHei',
              resize: 'none',
              padding: '10px',
              borderRadius: 8,
              border: '1px solid #ccc',
              lineHeight: 1.6,
              boxSizing: 'border-box',
              fontSize: '0.95rem',
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{newCommentText.length}/1000</Typography>
            <Button variant="contained" endIcon={<Send />} onClick={handlePostComment} size="small">
              發布
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          請 <Link to="/signin" state={{ from: location }}>登入</Link> 後再評論。
        </Typography>
      )}

      {/* 評論列表 */}
      {comments.length > 0 ? (
        comments.map(comment => (
          <Box key={comment.id} sx={{ mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle2" color="primary.main" sx={{ fontFamily: 'Microsoft JhengHei' }}>
                {comment.user?.username || '匿名用戶'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {utc2local(comment.created_at)}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{
              my: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontFamily: 'Noto Sans CJK, Microsoft JhengHei, Arial, sans-serif', fontSize: '1rem'
            }}>
              {comment.comment_text}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => handleVote(comment.id, true)}><ThumbUp fontSize="small" /></IconButton>
              <Typography variant="caption">{comment.likes ?? 0}</Typography>
              <IconButton size="small" onClick={() => handleVote(comment.id, false)}><ThumbDown fontSize="small" /></IconButton>
              <Typography variant="caption">{comment.dislikes ?? 0}</Typography>
            </Box>
          </Box>
        ))
      ) : (
        !isLoading && <Typography variant="body2" align="center" color="text.secondary" sx={{ py: 3 }}>暫無評論。</Typography>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>
      )}

      {hasMore && !isLoading && comments.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <IconButton onClick={() => fetchComments(page + 1)}><MoreHoriz /></IconButton>
        </Box>
      )}
    </Box>
  );
}

// ── 主組件 ────────────────────────────────────────────────────────────────────
export default function SingleNewsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tweet, setTweet] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    setIsAuthenticated(checkAuthStatus());
    const fetchSingleTweet = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${server}/api/tweets/${id}`);
        if (!res.ok) throw new Error('Data not found');
        const data = await res.json();
        setTweet(data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchSingleTweet();
  }, [id]);

  React.useEffect(() => {
    document.body.style.backgroundColor = '#f6f4ea';
    document.body.style.backgroundImage = `
      radial-gradient(1000px 600px at 20% -10%, rgba(255,255,255,0.7), rgba(255,255,255,0) 60%),
      radial-gradient(900px 500px at 120% 0%, rgba(255,255,255,0.55), rgba(255,255,255,0) 65%),
      linear-gradient(180deg, rgba(0,0,0,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
    `;
    document.body.style.backgroundSize = `auto, auto, 100% 28px, 28px 100%`;
    document.body.style.minHeight = '100vh';
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.body.style.minHeight = '';
    };
  }, []);

  if (loading) {
    return <Box sx={{ minHeight: "100vh", display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></Box>;
  }

  if (error || !tweet) {
    return (
      <Box sx={{ minHeight: "100vh", textAlign: 'center', pt: 20 }}>
        <Typography variant="h5" gutterBottom>找不到該篇新聞</Typography>
        <Button variant="contained" onClick={() => navigate('/news')} sx={{ mt: 2 }}>返回新聞列表</Button>
      </Box>
    );
  }

  const cleanText = tweet.tweet_text ? tweet.tweet_text.replace(/[\r\n]+/g, " ") : "財經新聞";

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Helmet>
        <title>{tweet.agency} 財經快訊 | 最新動態</title>
        <meta name="description" content={tweet.tweet_text ? tweet.tweet_text.substring(0, 150) : "全球宏觀經濟與市場動態即時解析"} />
        <meta property="og:title" content={`${tweet.agency} 財經快訊`} />
        <meta property="og:description" content={tweet.tweet_text ? tweet.tweet_text.substring(0, 150) : ""} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href },
          "headline": cleanText.substring(0, 100) + "...",
          "datePublished": tweet.created_at,
          "author": { "@type": "Organization", "name": tweet.agency || "未知機構" },
          "description": cleanText
        })}</script>
      </Helmet>

      <AppAppBar />

      <Container maxWidth="md" sx={{ mt: 16.5, mb: 6 }}>
        {/* 新聞內容 */}
        <Box component="article" sx={{
          backgroundColor: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(12px)",
          borderRadius: "16px",
          boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
        }}>
          <Typography variant="subtitle1" sx={{ color: 'skyblue', fontWeight: 'bold', fontFamily: 'Microsoft JhengHei', mb: 2 }}>
            {tweet.agency}
          </Typography>

          <TitleTypography component="h1" variant="h5" color="#1a1a1a" sx={{ mb: 3, lineHeight: 1.6 }}>
            {renderTextWithLinks(tweet.tweet_text)}
            {tweet.txt_tw && (
              <>
                <br /><br />
                <Typography component="span" variant="h6" color="#444444" sx={{ fontFamily: 'Microsoft JhengHei' }}>
                  【中文正體】 {renderTextWithLinks(tweet.txt_tw)}
                </Typography>
              </>
            )}
          </TitleTypography>

          <Typography component="time" dateTime={new Date(tweet.created_at).toISOString()}
            variant="body2" sx={{ color: 'darkgreen', display: 'block', fontFamily: 'Microsoft JhengHei' }}>
            發布時間：{utc2local(tweet.created_at)}
          </Typography>

          {/* ✅ 評論區直接嵌入，不用彈窗 */}
          <InlineComments tweet={tweet} isAuthenticated={isAuthenticated} />
        </Box>
      </Container>
    </Box>
  );
}